import { Component, signal, ChangeDetectionStrategy } from '@angular/core';

import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SubscriptionService } from '../../subscription.service';
import { ToastrNotificationService } from 'src/app/shared/services/toastr-notification.service';
import { ToastrType } from 'src/app/shared/types/ToastrType.enum';
import { UrlParamsService } from 'src/app/shared/services/url-params.service';
import { catchError, map, Observable, of, Subscription } from 'rxjs';
import { MinProjectDTO } from '../../types/MinProjectDTO';
import { GlobalSearchStakeholderComponent } from '../../../../shared/components/global-search-stakeholder/global-search-stakeholder.component';
@Component({
  selector: 'app-search-subscription',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    GlobalSearchStakeholderComponent,
  ],
  templateUrl: './search-subscription.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './search-subscription.component.scss',
})
export class SearchSubscriptionComponent {
  projectInfo: MinProjectDTO | null = null;
  private subscription: Subscription | null = null;
  isStakeholderSearchValid: boolean = false;
  onStakeholderSearch: number = 0;
  isLoadingMatchingStakeholders: () => boolean;

  constructor(
    private subscriptionService: SubscriptionService,
    private toastrNotification: ToastrNotificationService,
    private urlParamsService: UrlParamsService
  ) {
    this.isLoadingMatchingStakeholders = () =>
      this.subscriptionService.isLoadingMatchingStakeholders$();
  }

  ngOnInit(): void {
    // this.subscriptionService.stakeGuid = null; // also needs to remove from url
    this.subscription = this.subscriptionService.projectInfo$.subscribe({
      next: (projectInfo) => {
        this.projectInfo = projectInfo;
      },
      error: (error) => {
        console.error('Error fetching project info:', error);
      },
    });
  }

  handleStakeholderSearchResult(event: { status: string; data: any; formValues: any }): void {
    switch (event.status) {
      case 'success':
        if (!event?.data?.StakeGuid) {
          this.toastrNotification.showMessage(ToastrType.Info, 'No Matching stakeholders found');
          this.urlParamsService.removeStakeGuid();
          this.urlParamsService.navigateWithParams('/subscription/edit', {
            LastName: event.formValues.LastName,
            Mail1ZipCode: event.formValues.Mail1ZipCode,
            StakeEmail: event.formValues.StakeEmail,
            isEdit: false,
          });
        } else {
          if (this.urlParamsService.projectGuid && event.data.StakeGuid) {
            const isSubscribed = event.data.IsReceiveEmailUpdate ?? true;
            this.triggerEmail(
              this.urlParamsService.projectGuid,
              event.data.StakeGuid,
              isSubscribed
            ).subscribe((success) => {
              if (success) {
                this.urlParamsService.navigateWithParams('/subscription/confirm');
              } else {
                this.urlParamsService.navigateToErrorPage(
                  'Error sending confirmation email. Please try again later.'
                );
              }
            });
          }
        }

        break;
      case 'error':
        console.error('Error during service call:', event.data);

        break;
      case 'invalid':
        console.error('Form is invalid:', event.data);
        break;
      default:
        console.warn('Unknown status:', event);
    }
  }

  handleFormValidity(event: boolean): void {
    this.isStakeholderSearchValid = event;
  }

  async onSubmit(): Promise<void> {
    // Emits a value to the stake search component to run the search API call
    // Triggers handleStakeholderSearchResult to run
    this.onStakeholderSearch++;
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  triggerEmail(projectGuid: string, stakeGuid: string, isSubscribed: boolean): Observable<boolean> {
    return this.subscriptionService
      .triggerConfirmationSubscriptionEmail(projectGuid, stakeGuid, isSubscribed)
      .pipe(
        map(() => true),
        catchError((error) => {
          console.error('Error sending confirmation email:', error);
          return of(false);
        })
      );
  }
}
